@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.InfrastrukturApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.utils.All
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div

@Composable
fun CUSParkplatzTorverwaltung(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()

    var parkplaetze by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var tore by remember { mutableStateOf<List<All>>(emptyList()) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }

    suspend fun loadData() {
        standorte = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        parkplaetze = letzteFilter?.let { InfrastrukturApi.searchParkplatzTore(it) }.orEmpty()
        tore = InfrastrukturApi.loadTore().filter { it.gesperrt != true }
    }

    LaunchedEffect(art) {
        loadData()
    }

    val standortIds = remember(parkplaetze, tore) {
        (parkplaetze.map { it.standort } + tore.map { it.standort })
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()
            .sortedWith(compareBy<String> { it.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it })
    }

    LaunchedEffect(standortIds) {
        if (selectedStandort.isNotBlank() && selectedStandort !in standortIds) {
            selectedStandort = ""
        }
    }

    if (parkplaetze.isEmpty() || tore.isEmpty()) return

    val gefilterteParkplaetze = parkplaetze.filter { selectedStandort.isBlank() || it.standort == selectedStandort }
    val torListe = tore
        .filter { selectedStandort.isBlank() || it.standort == selectedStandort }
        .mapNotNull { it.tor }
        .distinct()
        .sorted()

    if (gefilterteParkplaetze.isEmpty() || torListe.isEmpty()) return

    val headers = buildList {
        add("ID")
        add("Parkplatz")
        torListe.forEach { add("Tor $it") }
    }

    val data = gefilterteParkplaetze.map { parkplatz ->
        val row = buildList {
            add(parkplatz.id)
            add(parkplatz.platz.ifBlank { "P${parkplatz.id}" })
            torListe.forEach { tor ->
                add(if (parkplatz.tore.contains(tor)) "✅" else "⬜")
            }
        }
        "${parkplatz.standort}:${parkplatz.id}" to row
    }

    TabularScreen(
        screenKey = "ParkplatzTorverwaltung",
        rollenKey = "ParkplatzTorverwaltung",
        title = "Parkplatz und bevorzugte Tore",
        headers = headers,
        data = data,
        editable = true,
        nonEditableCols = setOf("ID", "Parkplatz"),
        toggleColumns = headers.drop(2).toSet(),
        extraButtons = {
            Div({ style { width(240.px) } }) {
                AppDropdown(
                    options = buildList {
                        add(All(key = "", label = "Alle Standorte"))
                        addAll(standortIds.map { id ->
                            val standort = standorte.firstOrNull { it.id == id }
                            All(
                                key = id,
                                label = standort?.standort?.takeIf { it.isNotBlank() } ?: "Standort $id"
                            )
                        })
                    },
                    selectedValue = selectedStandort,
                    onChange = { selectedStandort = it },
                    appearance = AppDropdownAppearance.Field,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                parkplaetze = InfrastrukturApi.searchParkplatzTore(filters)
            }
        },
        onCommit = { rows ->
            scope.launch {
                rows.forEach { (rowId, cols) ->
                    val standort = rowId.substringBefore(":")
                    val id = rowId.substringAfter(":", "")
                    val original = parkplaetze.firstOrNull { it.standort == standort && it.id == id } ?: return@forEach

                    val neueTore = mutableListOf<Int>()
                    torListe.forEachIndexed { index, torNummer ->
                        if (cols[index + 2] == "✅") {
                            neueTore.add(torNummer)
                        }
                    }

                    val updated = original.copy(tore = neueTore.sorted())
                    if (original != updated) {
                        InfrastrukturApi.saveParkplatz(updated)
                    }
                }
                loadData()
            }
        }
    )
}
